/**
 * SPA Navigation module for Datastar-based navigation
 * Enables smooth page transitions without full page reloads
 *
 * Uses Datastar's native SSE handling via a hidden trigger element
 */

import type { RouteConfig, RoutesMap } from './types';

// Route configuration: URL path -> SSE endpoint and page identifier
const ROUTES: RoutesMap = {
    '/app/groups': { endpoint: '/ds/spa/groups', page: 'groups' },
    '/app/gear': { endpoint: '/ds/spa/gear', page: 'gear' },
    '/app/routes': { endpoint: '/ds/spa/routes', page: 'routes' },
    '/app/trips': { endpoint: '/ds/spa/trips', page: 'trips' },
};

interface HistoryState {
    path: string;
    page: string;
}

/**
 * SPANav class handles client-side navigation for app pages
 */
export class SPANav {
    private navLinkSelector = '[data-spa-nav]';
    private isNavigating = false;

    /**
     * Initialize SPA navigation listeners
     */
    init(): void {
        // Handle nav link clicks
        document.addEventListener('click', (e) => this.handleClick(e));

        // Handle browser back/forward
        window.addEventListener('popstate', (e) => this.handlePopState(e as PopStateEvent));

    }

    /**
     * Handle click events on navigation links
     */
    handleClick(e: Event): void {
        const target = e.target as HTMLElement;
        const link = target.closest(this.navLinkSelector) as HTMLAnchorElement | null;
        if (!link) return;

        const href = link.getAttribute('href');
        if (!href) return;

        const route = ROUTES[href];

        if (!route) return; // Not a SPA route, let normal navigation happen

        e.preventDefault();

        // Don't navigate if we're already on this page
        if (window.location.pathname === href) return;

        this.navigate(href, route, true);
    }

    /**
     * Navigate to a new page
     */
    navigate(path: string, route: RouteConfig, pushState = true): void {
        if (this.isNavigating) return;
        this.isNavigating = true;

        // Update URL first
        if (pushState) {
            const state: HistoryState = { path, page: route.page };
            history.pushState(state, '', path);
        }

        // Update active nav state
        this.updateActiveNav(route.page);

        // Close mobile menu if open
        const mobileMenu = document.getElementById('mobile-menu');
        if (mobileMenu) {
            mobileMenu.classList.add('hidden');
        }

        // Add loading class for fade-out effect
        const content = document.getElementById('app-content');
        if (content) {
            content.classList.add('spa-loading');
            content.classList.remove('spa-loaded');
        }

        // Use Datastar to fetch and apply the content
        // We trigger a click on a hidden element that has the data-on:click attribute
        const trigger = document.getElementById('spa-nav-trigger') as HTMLElement | null;
        if (trigger) {
            trigger.setAttribute('data-endpoint', route.endpoint);
            trigger.click();
        }

        // After content loads, add fade-in effect
        // We use a MutationObserver to detect when content changes
        if (content) {
            const observer = new MutationObserver(() => {
                observer.disconnect(); // Disconnect FIRST to prevent multiple fires
                if (!content.classList.contains('spa-loaded')) {
                    content.classList.remove('spa-loading');
                    content.classList.add('spa-loaded');
                }
                this.isNavigating = false;
            });
            observer.observe(content, { childList: true, subtree: true });
        } else {
            this.isNavigating = false;
        }
    }

    /**
     * Update active navigation state
     */
    updateActiveNav(page: string): void {
        // Update desktop nav links
        document.querySelectorAll(this.navLinkSelector).forEach(link => {
            const href = link.getAttribute('href');
            const isActive = href === `/app/${page}`;

            // Toggle classes for desktop nav
            if (link.classList.contains('nav-link')) {
                link.classList.toggle('active', isActive);
                link.classList.toggle('text-base-content', isActive);
            }

            // Toggle classes for mobile nav
            if (link.classList.contains('mobile-nav-link')) {
                if (isActive) {
                    link.classList.add('bg-emerald/10', 'text-base-content');
                    link.classList.remove('text-base-content/60');
                } else {
                    link.classList.remove('bg-emerald/10', 'text-base-content');
                    link.classList.add('text-base-content/60');
                }
            }
        });
    }

    /**
     * Handle browser back/forward navigation
     */
    handlePopState(e: PopStateEvent): void {
        const state = e.state as HistoryState | null;
        const path = state?.path || window.location.pathname;
        const route = ROUTES[path];

        if (route) {
            this.navigate(path, route, false);
        } else {
            // Not a SPA route, do full reload
            window.location.reload();
        }
    }
}

